import _ from "lodash";
import React from "react";
import { Modal, EmptyState } from "@toddle-edu/ds-web";
import { ASSESSMENT_TOOLS } from "@toddle-edu/common-utils";
import { getMYPObjectiveRubricOptions } from "modules/Services";
import classes from "./AssessmentToolModal.scss";
import { colors } from "Constants";
import { getFormattedHtml } from "Worksheet/modules/WorksheetUtils";
import {
  NoChartIllustration,
  NoChecklistIllustration,
} from "@toddle-edu/ds-theme";
import { useI18n } from "Hooks";

import ToolResponse from "AssessmentEvaluation/routes/EvaluationDetails/routes/AssessmentInsight/components/ToolResponse";
import { getFilteredStudentListMemoize } from "ClassRoom/routes/ClassRoomDetails/components/TeacherClassRoomDetails/components/AssignmentEvaluationInsight/Utils";
import SubHeader from "AssessmentEvaluation/routes/EvaluationDetails/routes/AssessmentInsight/components/SubHeader";

const INSIGHT_TABS = [
  {
    label: "Teacher responses",
    locale: "common:overview",
    value: "teacherResponse",
    responseCount: "teacherResponseCount",
    color: colors.blue400,
    studentsList: "studentsByTeacher",
  },
  {
    label: "Student responses",
    locale: "common:student_evaluation",
    value: "studentResponse",
    responseCount: "studentResponseCount",
    color: colors.yellow500,
    studentsList: "studentsByStudent",
  },
];

const styles = {
  rubricInnerConStyle: {
    width: "unset",
  },
  tooltipInnerStyle: {
    maxHeight: "208px",
    overflowY: "scroll",
  },
};
const emptyStateSubtitleStyle = {
  maxWidth: "544px",
  lineHeight: "24px",
};

const getResponseInsight = ({
  insights,
  currentInsightResponse,
  currentInsightStudents,
}) => {
  return _.map(insights, insight => {
    return {
      ..._.omit(insight, [
        "studentResponseCount",
        "teacherResponseCount",
        "studentsByTeacher",
        "studentsByStudent",
      ]),
      responseCount: insight[currentInsightResponse],
      studentsList: insight[currentInsightStudents],
    };
  });
};

const AssessmentToolModal = ({
  showAssessmentToolsModal,
  closeAssessmentToolsModal,
  assessmentTools,
  toolInsights,
  assignedStudents,
  label,
  labelIcon,
  assessmentToolsEvaluationDetails,
}) => {
  const { t } = useI18n(["common", "worksheet"]);

  const renderAssessmentTools = ({ assessmentTools, toolInsights }) => {
    const assessmentToolWithInsights = _.filter(
      assessmentTools?.edges,
      ({ node }) => {
        const { assessmentToolType } = node;

        const toolData = _.find(
          ASSESSMENT_TOOLS,
          ({ type }) => type === assessmentToolType
        );
        return _.get(toolData, "hasInsight", false);
      }
    );

    return _.map(assessmentToolWithInsights, ({ node }) => {
      const {
        id,
        assessmentToolType,
        assessmentToolItem,
        isTeacherEvaluationEnable,
        isStudentEvaluationEnable,
      } = node || {};

      const insightsData = _.find(
        toolInsights,
        ({ toolId }) => toolId === assessmentToolItem?.id
      );

      return (
        <div key={id} className={classes.assessmentToolCardContainer}>
          {renderAssessmentToolCard({
            toolId: _.get(assessmentToolItem, "id"),
            toolType: assessmentToolType,
            insights: _.get(insightsData, "insights", []),
            assessmentTool: assessmentToolItem,
            isTeacherEvaluationEnable,
            isStudentEvaluationEnable,
          })}
        </div>
      );
    });
  };

  const renderAssessmentToolCard = ({
    toolId,
    insights,
    toolType,
    assessmentTool,
    isTeacherEvaluationEnable,
    isStudentEvaluationEnable,
  }) => {
    if (!isTeacherEvaluationEnable && !isStudentEvaluationEnable) return;

    const currentTab = "teacherResponse";
    const currentInsightTab = _.find(INSIGHT_TABS, { value: currentTab });

    let emptyStateIllustration = NoChartIllustration;

    switch (toolType) {
      case "RUBRIC":
        emptyStateIllustration = NoChecklistIllustration;
        break;
      case "CHECKLIST":
        emptyStateIllustration = NoChecklistIllustration;
        break;
      default:
        emptyStateIllustration = NoChartIllustration;
    }

    const assessmentToolTypeData = _.find(
      ASSESSMENT_TOOLS,
      ({ type }) => type === toolType
    );

    const assessmentToolTypeLocale = _.get(assessmentToolTypeData, "locale");

    const filteredStudentCount = getFilteredStudentListMemoize({
      assignedStudents,
      responseType: currentTab,
      toolId,
      t,
    });

    const done = _.get(filteredStudentCount, "done", 0);

    return (
      <>
        <SubHeader
          toolType={toolType}
          filteredStudentCount={filteredStudentCount}
        />
        {done > 0 ? (
          <ToolResponse
            toolType={toolType}
            isData={true}
            isLoading={false}
            assessmentTool={assessmentTool}
            insightColor={currentInsightTab.color}
            insights={getResponseInsight({
              insights,
              currentInsightResponse: _.get(
                currentInsightTab,
                "responseCount",
                ""
              ),
              currentInsightStudents: _.get(
                currentInsightTab,
                "studentsList",
                ""
              ),
            })}
            rubricInnerConStyle={styles.rubricInnerConStyle}
            shouldTakeFullWidth={true}
            mypObjectiveRubricOptions={getMYPObjectiveRubricOptions({
              unitPlanFields: _.get(
                assessmentToolsEvaluationDetails,
                "unitPlanFields",
                []
              ),
              field_list: _.get(
                assessmentToolsEvaluationDetails,
                "field_list",
                {}
              ),
              assessmentFields: _.get(
                assessmentToolsEvaluationDetails,
                "assessmentFields",
                []
              ),
              measureAssessingValue: toolType,
            })}
          />
        ) : (
          <div className={classes.emptyStateContainer}>
            <EmptyState
              illustration={emptyStateIllustration}
              title={t("classRoom:no_insights_yet_with_label", {
                label: t(assessmentToolTypeLocale),
              })}
              subtitle={
                currentTab === "teacherResponse"
                  ? t("classRoom:insights_not_eval_subtext_with_label", {
                      label: t(assessmentToolTypeLocale),
                    })
                  : t("classRoom:once_students_are_complete_self_eval", {
                      label: t(assessmentToolTypeLocale),
                    })
              }
              subtitleStyle={emptyStateSubtitleStyle}
            />
          </div>
        )}
      </>
    );
  };

  return (
    <Modal
      width="1312px"
      height="788px"
      isOpen={showAssessmentToolsModal}
      onClose={closeAssessmentToolsModal}
      shouldCloseOnEsc
      shouldCloseOnOverlayClick
    >
      <Modal.Header isClosable>
        <div className={classes.modalHeader}>
          {labelIcon}
          <span className={classes.modalTitle} dir="auto">
            {getFormattedHtml({ htmlString: label, t })}
          </span>
        </div>
      </Modal.Header>
      <Modal.Body isScrollable>
        {renderAssessmentTools({ assessmentTools, toolInsights })}
      </Modal.Body>
    </Modal>
  );
};

export default AssessmentToolModal;
